import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useRef, useState } from 'react';
import { useStore } from 'src/stores';
const POLL_INTERVAL_MS = 5000;
/**
 * About ten minutes. Extraction settles a bill well within that, so one still unsettled by then is one this read can't
 * see -- more than 100 bills arrived after it -- and asking again won't find it.
 */
const MAX_POLLS = 120;
const MAX_FAILED_POLLS = 3;

/**
 * Keeps the upload dialog's statuses moving (LB-6034). The dialog reads each file's bill from what the page knows, and
 * the table keeps only its own page up to date, which a large upload reaches past. So while the dialog is open and any
 * of its bills is still being read, the company's newest bills are read every few seconds.
 *
 * Quiet: the table keeps its own poll, which says when it stops. Chained, as that one is, and a read still out is
 * waited for rather than replaced, since a second `run` would cancel it.
 *
 * @param isOpen whether the dialog is open
 * @param billIds the bills its files became
 */
export const useUploadStatusPoll = _ref => {
  let isOpen = _ref.isOpen,
    billIds = _ref.billIds;
  const _useStore = useStore(),
    companyBills = _useStore.companyBills;
  const isWaiting = billIds.some(billId => !companyBills.isBillSettled(billId));

  // Read by the running poll rather than restarting it: during a large upload a file lands every few seconds, and a
  // restart on each would put the next read off every time.
  const isWaitingRef = useRef(isWaiting);
  isWaitingRef.current = isWaiting;
  const polls = useRef(0);
  const failedPolls = useRef(0);
  const isPolling = useRef(false);
  const _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    restarts = _useState2[0],
    setRestarts = _useState2[1];

  // A new file landing, or the dialog opening again, is new work, with a new allowance. A poll that has stopped starts
  // again for it; one still running keeps its schedule.
  useEffect(() => {
    polls.current = 0;
    failedPolls.current = 0;
    if (!isPolling.current) setRestarts(count => count + 1);
  }, [isOpen, billIds.length]);
  useEffect(() => {
    const hasGivenUp = () => polls.current >= MAX_POLLS || failedPolls.current >= MAX_FAILED_POLLS;
    if (!isOpen || !isWaiting || hasGivenUp()) return undefined;
    let isActive = true;
    let timeoutId = null;
    const poll = async () => {
      var _resp;
      const scheduleNext = () => {
        isPolling.current = isActive && isWaitingRef.current && !hasGivenUp();
        if (isPolling.current) timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
      };
      if (companyBills.getRecentBillsAsync.inProgress) {
        scheduleNext();
        return;
      }
      polls.current += 1;
      let resp;
      try {
        resp = await companyBills.getRecentBillsAsync.run(undefined, []);
      } catch (_unused) {
        resp = undefined;
      }
      if (!isActive) return;
      failedPolls.current = (_resp = resp) !== null && _resp !== void 0 && _resp.isSuccessful ? 0 : failedPolls.current + 1;
      scheduleNext();
    };
    isPolling.current = true;
    timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      isPolling.current = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, isWaiting, restarts, companyBills]);
};